import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import PlaceholderImage from '../../images/7.jpg';
import PageHeader from '../common/PageHeader';

const Announcements = () => {
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeCategory, setActiveCategory] = useState('all');

  useEffect(() => {
    const fetchAnnouncements = async () => {
      try {
        // In a real implementation, this would fetch from the API
        // For now, using mock data
        const mockAnnouncements = [
          {
            id: 1,
            title: "Sunday Service Time Change",
            content: "Starting next week, our Sunday service will begin at 9:30 AM instead of 10:00 AM. This change is being made to accommodate our growing congregation and to provide more time for fellowship after the service.",
            date: "2023-06-15",
            image: PlaceholderImage,
            category: "Service Update"
          },
          {
            id: 2,
            title: "Youth Camp Registration Open",
            content: "Registration for our annual youth summer camp is now open. Early bird discounts available until June 30th. The camp will be held from July 15-22 at Mountain View Retreat Center. Activities include hiking, swimming, team building, and daily Bible study sessions.",
            date: "2023-06-10",
            image: PlaceholderImage,
            category: "Youth Ministry"
          },
          {
            id: 3,
            title: "Community Outreach Event",
            content: "Join us this Saturday for our monthly community service project. We'll be cleaning up the local park and providing free meals to those in need. Meet at the church parking lot at 8:00 AM. All are welcome to participate!",
            date: "2023-06-05",
            image: PlaceholderImage,
            category: "Outreach"
          },
          {
            id: 4,
            title: "New Bible Study Series",
            content: "We're starting a new Bible study series on the Book of Romans next Wednesday at 7:00 PM. This 12-week series will explore Paul's letter to the Romans and its relevance for today. Study guides are available at the church office.",
            date: "2023-06-01",
            image: PlaceholderImage,
            category: "Bible Study"
          },
          {
            id: 5,
            title: "Worship Team Auditions",
            content: "Our worship team is looking for new members! Auditions will be held on June 20th from 6:00-8:00 PM. We're seeking vocalists, musicians, and sound technicians. Please contact the church office to schedule an audition time.",
            date: "2023-05-28",
            image: PlaceholderImage,
            category: "Music Ministry"
          },
          {
            id: 6,
            title: "Marriage Enrichment Workshop",
            content: "Join us for a one-day marriage enrichment workshop on June 25th from 9:00 AM to 4:00 PM. This workshop is designed to strengthen your marriage through biblical principles and practical exercises. Childcare will be provided.",
            date: "2023-05-25",
            image: PlaceholderImage,
            category: "Family Ministry"
          }
        ];
        
        setAnnouncements(mockAnnouncements);
        setLoading(false);
      } catch (err) {
        console.error('Error fetching announcements:', err);
        setError('Failed to load announcements. Please try again later.');
        setLoading(false);
      }
    };

    fetchAnnouncements();
  }, []);

  // Get unique categories for filter
  const categories = ['all', ...new Set(announcements.map(announcement => announcement.category))];

  // Filter announcements based on active category
  const filteredAnnouncements = activeCategory === 'all' 
    ? announcements 
    : announcements.filter(announcement => announcement.category === activeCategory);

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 p-4 rounded-md">
        <p className="text-red-700">{error}</p>
      </div>
    );
  }

  return (
    <main className="container mx-auto px-4 py-8">
      <PageHeader 
        title="Announcements" 
        subtitle="Stay updated with the latest news and events from our church"
        imageUrl={PlaceholderImage}
        imageAlt="Announcements Header"
      />

      {/* Category Filter */}
      <div className="mb-8 mt-5">
        <div className="flex flex-wrap gap-2">
          {categories.map((category) => (
            <button
            className={`px-4 py-2 rounded-full ${
              activeCategory === category
                ? 'bg-indigo-600 text-white'
                : 'bg-gray-200 text-gray-800 hover:bg-gray-300'
            }`}
            onClick={() => setActiveCategory(category)}
          >
            {category}
          </button>
          ))}
        </div>
      </div>

      <div className="bg-white shadow-md rounded-lg overflow-hidden">
        <div className="p-6">
          <div className="space-y-6">
            {filteredAnnouncements.length > 0 ? (
              filteredAnnouncements.map((announcement) => (
                <div key={announcement.id} className="border-b border-gray-200 pb-6 last:border-b-0 last:pb-0">
                  <div className="flex flex-col md:flex-row gap-4">
                    <div className="md:w-1/4">
                      <img 
                        src={announcement.image} 
                        alt={announcement.title} 
                        className="w-full h-48 object-cover rounded-lg shadow-md"
                      />
                    </div>
                    <div className="md:w-3/4">
                      <div className="flex items-center mb-2">
                        <span className="inline-block bg-indigo-100 text-indigo-800 text-xs font-medium px-2.5 py-0.5 rounded">
                          {announcement.category}
                        </span>
                        <span className="text-gray-500 text-sm ml-3">
                          {new Date(announcement.date).toLocaleDateString('en-US', { 
                            year: 'numeric', 
                            month: 'long', 
                            day: 'numeric' 
                          })}
                        </span>
                      </div>
                      <h3 className="text-xl font-semibold text-gray-900 mb-2">{announcement.title}</h3>
                      <p className="text-gray-700 mb-4">{announcement.content}</p>
                      <div className="flex flex-wrap gap-3">
                        <Link 
                          to={`/announcements/${announcement.id}`} 
                          className="text-indigo-600 hover:text-indigo-800 font-medium flex items-center"
                        >
                          Read more
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 ml-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                          </svg>
                        </Link>
                        <button className="text-gray-500 hover:text-indigo-600 flex items-center">
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
                          </svg>
                          Share
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center py-8">
                <p className="text-gray-500">No announcements found in this category.</p>
              </div>
            )}
          </div>
          
          <div className="mt-8 text-center">
            <Link 
              to="/announcements" 
              className="inline-block bg-indigo-600 text-white px-6 py-2 rounded-md hover:bg-indigo-700 transition duration-200"
            >
              View All Announcements
            </Link>
          </div>
        </div>
      </div>
    </main>
  );
};

export default Announcements;
